import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
} from "@mui/material";
import { useState } from "react";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#449095" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#449095" },
};

const COMPRESSION_OPTIONS    = ["gzip", "snappy", "lz4", "zstd", "none"];
const SECURITY_PROTOCOL_OPTIONS = ["PLAINTEXT", "SSL", "SASL_PLAINTEXT", "SASL_SSL"];
const SASL_MECHANISM_OPTIONS = ["PLAIN", "SCRAM-SHA-256", "SCRAM-SHA-512"];

const TRIGGER_TOPICS = [
  { key: "critical",          label: "Critical",            color: "#931A1D", defaultTopic: "security-critical" },
  { key: "high",              label: "High",                color: "#FEA51D", defaultTopic: "security-high" },
  { key: "alert",             label: "Alert",               color: "#D1C167", defaultTopic: "security-alert" },
  { key: "movements",         label: "Movements",           color: "#459296", defaultTopic: "security-movements" },
  { key: "threat",            label: "Threat Intelligence", color: "#C485BF", defaultTopic: "security-threat" },
  { key: "security_cases",    label: "Security Cases",      color: "#E67E22", defaultTopic: "security-cases" },
  { key: "tiresia_anomalies", label: "Tiresia Anomalies",   color: "#7C4DFF", defaultTopic: "tiresia-anomalies" },
];

const INITIAL = {
  bootstrap_servers: "",
  compression_type:  "gzip",
  security_protocol: "SASL_SSL",
  tls:  { enabled: true,  verify: false },
  sasl: { enabled: true,  mechanism: "PLAIN", username: "", password: "" },
  topics: Object.fromEntries(
    TRIGGER_TOPICS.map(({ key, defaultTopic }) => [key, { enabled: false, topic: defaultTopic }])
  ),
};

const KafkaConfig = () => {
  const [cfg, setCfg] = useState(INITIAL);

  const setTop = (key, field, val) =>
    setCfg((prev) => ({
      ...prev,
      topics: { ...prev.topics, [key]: { ...prev.topics[key], [field]: val } },
    }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ── Header ────────────────────────────────────────────── */}
      <Box>
        <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Kafka</Typography>
        <Typography variant="caption" color="text.secondary">
          Stream security events to Apache Kafka topics. Each event type can be
          routed to a dedicated topic.
        </Typography>
      </Box>

      <Divider />

      {/* ── Broker ────────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Broker
        </Typography>
        <TextField
          size="small" label="Bootstrap Servers" fullWidth
          value={cfg.bootstrap_servers}
          onChange={(e) => setCfg((p) => ({ ...p, bootstrap_servers: e.target.value }))}
          placeholder="broker1:9092, broker2:9092"
          helperText="Comma-separated list of host:port pairs"
          sx={{ mb: 1.5, "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.85rem" } }}
        />
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            select size="small" label="Compression"
            value={cfg.compression_type}
            onChange={(e) => setCfg((p) => ({ ...p, compression_type: e.target.value }))}
            sx={{ minWidth: 180, flex: 1 }}
          >
            {COMPRESSION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Security Protocol"
            value={cfg.security_protocol}
            onChange={(e) => setCfg((p) => ({ ...p, security_protocol: e.target.value }))}
            sx={{ minWidth: 200, flex: 1 }}
          >
            {SECURITY_PROTOCOL_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Stack>
      </Box>

      {/* ── TLS ───────────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          TLS
        </Typography>
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            label="Enable TLS"
            control={
              <Switch checked={cfg.tls.enabled} sx={switchSx}
                onChange={(e) => setCfg((p) => ({ ...p, tls: { ...p.tls, enabled: e.target.checked } }))} />
            }
          />
          <FormControlLabel
            label="Verify Certificates"
            control={
              <Switch checked={cfg.tls.verify} disabled={!cfg.tls.enabled} sx={switchSx}
                onChange={(e) => setCfg((p) => ({ ...p, tls: { ...p.tls, verify: e.target.checked } }))} />
            }
          />
        </Stack>
      </Box>

      {/* ── SASL ──────────────────────────────────────────────── */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          SASL
        </Typography>
        <FormControlLabel
          label="Enable SASL"
          control={
            <Switch checked={cfg.sasl.enabled} sx={switchSx}
              onChange={(e) => setCfg((p) => ({ ...p, sasl: { ...p.sasl, enabled: e.target.checked } }))} />
          }
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            select size="small" label="Mechanism"
            value={cfg.sasl.mechanism} disabled={!cfg.sasl.enabled}
            onChange={(e) => setCfg((p) => ({ ...p, sasl: { ...p.sasl, mechanism: e.target.value } }))}
            sx={{ minWidth: 200, flex: 1 }}
          >
            {SASL_MECHANISM_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField
            size="small" label="Username"
            value={cfg.sasl.username} disabled={!cfg.sasl.enabled}
            onChange={(e) => setCfg((p) => ({ ...p, sasl: { ...p.sasl, username: e.target.value } }))}
            sx={{ minWidth: 180, flex: 1 }}
          />
          <TextField
            size="small" label="Password" type="password"
            value={cfg.sasl.password} disabled={!cfg.sasl.enabled}
            onChange={(e) => setCfg((p) => ({ ...p, sasl: { ...p.sasl, password: e.target.value } }))}
            sx={{ minWidth: 180, flex: 1 }}
          />
        </Stack>
      </Box>

      {/* ── Topics ────────────────────────────────────────────── */}
      <Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#aaa", textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: "0.08em" }}>
          Trigger on · Topic mapping
        </Typography>
        <Stack spacing={1.25}>
          {TRIGGER_TOPICS.map(({ key, label, color }) => {
            const t = cfg.topics[key];
            return (
              <Stack key={key} direction="row" spacing={1.5} alignItems="center">
                <Box
                  onClick={() => setTop(key, "enabled", !t.enabled)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.25, py: 0.5, borderRadius: "6px", cursor: "pointer",
                    minWidth: 180, flexShrink: 0,
                    border: `1px solid ${t.enabled ? color : "#333"}`,
                    backgroundColor: t.enabled ? `${color}18` : "transparent",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: color },
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: t.enabled ? color : "#444", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.78rem", color: t.enabled ? color : "#666", fontWeight: t.enabled ? 700 : 400 }}>
                    {label}
                  </Typography>
                </Box>
                <TextField
                  size="small" label="Topic" fullWidth
                  value={t.topic}
                  disabled={!t.enabled}
                  onChange={(e) => setTop(key, "topic", e.target.value)}
                  placeholder={`e.g. ${key.replace("_", "-")}`}
                  sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.82rem" } }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>

    </Box>
  );
};

export default KafkaConfig;
